/**
 * 成果物の表（機能一覧・バリデーション一覧・総合テスト）を読む形で出す器。
 *
 * **3 つの表を同じ見た目に揃えるために切り出す。** それぞれで組むと、枠線のある表と無い表が
 * 混ざって「どこが 1 行か」が読み分けられなくなる。行が 7〜9 行あって列も多いので、
 * 枠線・見出しの地色・1 行おきの色を付けて、目で行を追えるようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の成果物の表を、枠線と見出しの地色を揃えた読み取り用テーブルとして描く
 * @graph-connects none
 */

export interface SpecTableProps {
  /** 列の見出し。並びはそのまま列の並びになる */
  headings: readonly string[];
  /** 行。1 行はセルの配列で、`headings` と同じ数だけ並べる */
  rows: readonly { key: string; cells: readonly string[] }[];
}

/**
 * 読み取り用の表。
 * @graph-connects none
 */
export function SpecTable({ headings, rows }: SpecTableProps) {
  return (
    <div className="mt-3 overflow-x-auto border border-[var(--color-border)] rounded-lg">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[var(--color-bg-hover)]">
            {headings.map((heading) => (
              <th
                key={heading}
                className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.key}
              className={
                index % 2 === 1 ? 'bg-[var(--color-bg-elevated)]' : 'bg-[var(--color-bg-surface)]'
              }
            >
              {row.cells.map((cell, column) => (
                <td
                  // 同じ行に同じ文だけのセルが並ぶことがあるので、列の位置で鍵を作る
                  key={`${row.key}/${String(column)}`}
                  className="align-top px-3 py-2 border-t border-[var(--color-border)] text-[var(--color-text-primary)]"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
